import { useState, useRef, useEffect } from "react";

// Shared, UI-agnostic column-resize hook.
// Tracks a per-column pixel override (columnKey -> px) that survives only for
// the current session (state resets on unmount / sub-tab switch / reload).
// No external dependency: uses native pointer events wired at drag start.

const MIN_COLUMN_WIDTH = 122;
const MAX_COLUMN_WIDTH = 800;
// Breathing room kept between the widened column and the viewport edge so a
// dragged column can never grow wider than the screen can show.
const VIEWPORT_GUTTER = 48;

const clampWidth = (px) => {
  let maxAllowed = MAX_COLUMN_WIDTH;
  if (typeof window !== "undefined" && window.innerWidth) {
    const viewportMax = window.innerWidth - VIEWPORT_GUTTER;
    if (viewportMax < maxAllowed) {
      maxAllowed = viewportMax;
    }
  }
  if (maxAllowed < MIN_COLUMN_WIDTH) {
    maxAllowed = MIN_COLUMN_WIDTH;
  }
  let next = px;
  if (next < MIN_COLUMN_WIDTH) {
    next = MIN_COLUMN_WIDTH;
  }
  if (next > maxAllowed) {
    next = maxAllowed;
  }
  return Math.round(next);
};

const useColumnResize = () => {
  const [columnWidths, setColumnWidths] = useState({});
  const dragState = useRef(null);

  // If the component unmounts mid-drag (e.g. a sub-tab switch while dragging),
  // tear down the native listeners explicitly instead of relying on GC of the
  // removed handle node.
  useEffect(() => {
    return () => {
      const active = dragState.current;
      if (active && active.handleEl) {
        active.handleEl.removeEventListener("pointermove", active.onMove);
        active.handleEl.removeEventListener("pointerup", active.onEnd);
        active.handleEl.removeEventListener("pointercancel", active.onEnd);
      }
      dragState.current = null;
    };
  }, []);

  // Returns the pixel override for a column when one exists, otherwise the
  // caller-supplied default. This is what keeps untouched tables byte-identical:
  // with no override the default flows straight through.
  const getWidth = (columnKey, defaultPx) => {
    const current = columnWidths[columnKey];
    if (current !== undefined && current !== null) {
      return current;
    }
    return defaultPx;
  };

  const resetColumn = (columnKey) => {
    setColumnWidths((prev) => {
      if (prev[columnKey] === undefined) {
        return prev;
      }
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  };

  // Begins a drag. `handleEl` (the grip element) becomes the pointer-capture
  // target, so pointermove/pointerup fire on it even when the pointer leaves
  // the strip or the button is released off-element.
  const startResize = (columnKey, startWidthPx, event) => {
    if (!event) {
      return;
    }
    const handleEl = event.currentTarget;
    if (!handleEl) {
      return;
    }
    const startWidth =
      typeof startWidthPx === "number" && !isNaN(startWidthPx)
        ? startWidthPx
        : MIN_COLUMN_WIDTH;
    const startX = event.clientX;
    const pointerId = event.pointerId;

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const nextWidth = clampWidth(startWidth + delta);
      setColumnWidths((prev) => ({ ...prev, [columnKey]: nextWidth }));
    };

    const onEnd = (endEvent) => {
      handleEl.removeEventListener("pointermove", onMove);
      handleEl.removeEventListener("pointerup", onEnd);
      handleEl.removeEventListener("pointercancel", onEnd);
      try {
        if (
          endEvent &&
          typeof endEvent.pointerId === "number" &&
          handleEl.releasePointerCapture
        ) {
          handleEl.releasePointerCapture(endEvent.pointerId);
        }
      } catch (e) {
        // Releasing an already-released pointer can throw; safe to ignore.
      }
      dragState.current = null;
    };

    dragState.current = { columnKey, onMove, onEnd, handleEl };

    try {
      if (handleEl.setPointerCapture && typeof pointerId === "number") {
        handleEl.setPointerCapture(pointerId);
      }
    } catch (e) {
      // Capturing an invalid pointer id can throw; drag still works without it.
    }

    handleEl.addEventListener("pointermove", onMove);
    handleEl.addEventListener("pointerup", onEnd);
    handleEl.addEventListener("pointercancel", onEnd);
  };

  return { columnWidths, getWidth, startResize, resetColumn };
};

export default useColumnResize;
